import React, { useState, useEffect } from 'react';
import { Table, RefreshCw, Plus, MessageCircle } from 'lucide-react';
import { syncManager } from '../../services/sync';
import { useAuth } from '../../hooks/useAuth';
import { cjmsApi } from '../../views/CJMSView/api/cjmsApi';

const CommunicationHistory = ({ clientEmail, clientPhone, clientCustomerId }) => {
  const { user } = useAuth();
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        let targetEmail = user?.email;
        let targetPhone = null;

        if (clientEmail) {
          targetEmail = clientEmail;
          targetPhone = clientPhone;
        }

        if (!targetEmail) {
          setLoading(false);
          return;
        }

        // If we are in a client context, we don't necessarily need to find the customer record
        // unless we want to use Customer_ID for filtering.
        // Let's try to find the Customer_ID for the given email.
        const customers = await syncManager.fetchTabData('Customers', null, true);
        const customer = customers.find(c => c.Email === targetEmail || c.Customer_Email === targetEmail || c.email === targetEmail);
        let foundCustomerId = null;
        if (customer) {
          foundCustomerId = customer.Customer_ID || customer.id;
          setCustomerId(foundCustomerId);
        }

        // Fetch communications using the API to benefit from the improved filtering and sorting.
        const history = await cjmsApi.fetchCommunicationHistory(targetEmail, targetPhone, clientCustomerId);
        setCommunications(history);
        setLoading(false);
      } catch (err) {
        console.error('[CommunicationHistory] Load error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [user?.email, clientEmail, clientPhone]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const history = await cjmsApi.fetchCommunicationHistory(clientEmail || user?.email, clientPhone, clientCustomerId);
      setCommunications(history);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewMessage = async () => {
    const content = window.prompt("Enter message content:");
    if (!content) return;

    try {
      const targetEmail = clientEmail || user?.email;
      const targetPhone = clientPhone;

      await syncManager.logAction('Communications', 'add', {
        Comm_ID: `manual_${Date.now()}`,
        Customer_Name: clientEmail || user?.email || 'Unknown',
        Customer_ID: clientEmail || user?.email || 'Unknown',
        Channel: 'other',
        Timestamp: new Date().toISOString(),
        Direction: 'outgoing',
        Content: content,
        Message: content,
        Notes: 'Manually added message',
      });

      handleRefresh();
    } catch (err) {
      alert("Failed to add message: " + err.message);
    }
  };

  if (loading) {return <div className="text-center py-12">Loading communications...</div>;}
  if (error) {return <div className="text-center text-red-500 py-12">Error: {error}</div>;}

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Communication History</h2>
        <div className="flex gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 transition-colors"
          >
            <RefreshCw size={18} /> Refresh
          </button>
          {(clientEmail || user?.email) && (
             <button
              onClick={handleAddNewMessage}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
            >
              <Plus size={16} /> New Message
            </button>
          )}
        </div>
      </div>

      {communications.length === 0 ? (
        <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
          <MessageCircle size={32} className="mx-auto mb-2 opacity-20" />
          <p>No communications found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-slate-200">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-4 py-2 text-slate-500 font-medium text-sm">Date</th>
                <th className="text-left px-4 py-2 text-slate-500 font-medium text-sm">Message</th>
                <th className="text-left px-4 py-2 text-slate-500 font-medium text-sm">Source</th>
              </tr>
            </thead>
            <tbody>
              {communications.map((comm, index) => (
                <tr key={`${comm.timestamp}-${index}`} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-sm">
                    {comm.date}
                  </td>
                  <td className="px-4 py-3 text-slate-700 max-w-md break-words text-sm">
                    {comm.content}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-sm capitalize">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                        comm.direction === 'outgoing' ? 'bg-indigo-100 text-indigo-700' : 'bg-green-100 text-green-700'
                    }`}>
                        {comm.channel}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CommunicationHistory;
